import { Spinner } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { isFailedEvalStatus, isSucceededEvalStatus } from "@/modules/evaluation/public";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName, LoadingState } from "@/shared/ui/loadingState/public";
import { parseUtcMs } from "@/shared/utils/datetime/public";
import { EvalRunDetailSection } from "./EvalRunDetailElements";
import {
  EvalRunInfoSection,
  EvalRunMlflowLink,
  EvalRunPolicyInfoSection,
} from "./EvalRunInfoSections";
import { EvalRunFailureSection, EvalRunProgressSection } from "./EvalRunProgressSections";
import { EvalRunRecordedVideos } from "./EvalRunRecordedVideos";
import { EvalRunHeadlineStats, EvalRunResultSection } from "./EvalRunResultSection";
import { EvalRunScenarioSection } from "./EvalRunScenarioSection";
import { mlflowRunUrl } from "./evalRunPolicyModel";
import { IsaacEvalSection } from "./IsaacEvalSection";
import { resolveRunExecution, scenarioEnvironmentCount } from "./evalRunScenarioModel";
import type { EvalRunDetailController } from "./useEvalRunDetailController";
import styles from "./EvalRunDetailView.module.css";

export function EvalRunDetailView({
  controller,
}: {
  controller: EvalRunDetailController;
}): ReactNode {
  const {
    summary,
    detailState,
    progressState,
    metadataState,
    policiesState,
    metricsState,
    termsState,
    videosState,
    effectiveStatus,
  } = controller;
  const detail = detailState.status === "success" ? detailState.data : null;
  const progress = progressState.status === "success" ? progressState.data : null;
  const scenarioName = detail?.scenario ?? summary.scenario;
  const scenario = controller.scenario;
  const execution = resolveRunExecution(summary, detail, scenario);
  const policies = policiesState.status === "success" ? policiesState.data : null;
  const metrics = metricsState.status === "success" ? metricsState.data : null;
  const mlflowRunId = detail?.mlflow_run_id ?? metrics?.mlflow_run_id ?? null;
  const succeeded = isSucceededEvalStatus(effectiveStatus);
  const failed = isFailedEvalStatus(effectiveStatus);

  return (
    <article className={styles.root}>
      <header className={styles.detailHeader}>
        <div className={styles.headerIdentity}>
          <h2 className={styles.title}>상세</h2>
          <span className={styles.runId}>{summary.id}</span>
        </div>
        <div className={styles.headerMeta}>
          {/* 상세를 받기 전에는 여러 값이 `-` 다. 값이 없는 실행과 구분되도록 대기를 알린다. 머리 줄의
              오른쪽 칸에 둔다. 그래서 대기가 끝나도 아래 내용이 움직이지 않는다. */}
          {detailState.status === "loading" || detailState.status === "idle" ? (
            <span className={styles.pendingLine} role="status">
              <Spinner size="sm" className={inlineSpinnerClassName} />
              실행 상세를 불러오는 중입니다…
            </span>
          ) : null}
          {succeeded ? (
            <EvalRunMlflowLink runId={mlflowRunId} url={mlflowRunUrl(mlflowRunId, policies)} />
          ) : null}
        </div>
      </header>

      {succeeded ? (
        <EvalRunHeadlineStats
          summary={summary}
          metricsState={metricsState}
          baselineSeries={controller.baseline.series}
        />
      ) : null}

      <div className={styles.sections}>
        {succeeded ? (
          <IsaacEvalSection
            execution={execution}
            metadataState={metadataState}
            metricsState={metricsState}
            termsState={termsState}
            onRetry={controller.retry}
          />
        ) : null}
        <EvalRunScenarioSection
          scenarioName={scenarioName}
          datasetLabel={summary.upstream_dataset_label}
          datasetRegistryRef={summary.dataset_registry_ref}
          scenario={scenario}
          execution={execution}
          params={detail?.params ?? null}
          metadataState={metadataState}
          baseline={controller.baseline.series}
          runConfig={summary.config_name ?? null}
          onRetry={controller.retry}
          showSuccessCriteria={!succeeded}
        />

        {controller.isLive ? (
          <EvalRunProgressSection
            evalRunId={summary.id}
            progress={progress}
            status={effectiveStatus}
            startMs={parseUtcMs(detail?.started_at ?? summary.started_at ?? summary.created_at)}
            fallbackNumEnvs={scenarioEnvironmentCount(scenario)}
          />
        ) : null}

        {failed ? (
          <EvalRunFailureSection
            evalRunId={summary.id}
            status={effectiveStatus}
            observedError={progress?.observed_error ?? null}
          />
        ) : null}

        {succeeded ? (
          <div className={styles.successSections}>
            <EvalRunResultSection
              runId={summary.id}
              metricsState={metricsState}
              termsState={termsState}
              baseline={controller.baseline}
              onRetry={controller.retry}
            />
            <RecordedVideosSection videosState={videosState} onRetry={controller.retry} />
          </div>
        ) : null}

        <EvalRunPolicyInfoSection
          summary={summary}
          detail={detail}
          policies={policies}
          loadError={policiesState.status === "error"}
          onRetry={controller.retry}
        />
        <EvalRunInfoSection controller={controller} detail={detail} scenarioName={scenarioName} />
      </div>
    </article>
  );
}

function RecordedVideosSection({
  videosState,
  onRetry,
}: {
  videosState: EvalRunDetailController["videosState"];
  onRetry: () => void;
}): ReactNode {
  if (videosState.status === "loading" || videosState.status === "idle") {
    return (
      <EvalRunDetailSection title="녹화 영상">
        <LoadingState size="sm">영상을 불러오는 중입니다…</LoadingState>
      </EvalRunDetailSection>
    );
  }
  if (videosState.status === "error") {
    return (
      <EvalRunDetailSection title="녹화 영상">
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
          영상을 불러오지 못했습니다: {videosState.message.slice(0, 180)}
        </EmptyState>
      </EvalRunDetailSection>
    );
  }
  return <EvalRunRecordedVideos videos={videosState.data} />;
}
